/**
 * TUVI - Catalogo FFE
 * File manager conectado a MinIO bucket catalogo-ffe
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNotification } from '../../context/NotificationContext';
import { UPLOAD_URL } from '../../config';
// La tienda se carga al abrirla (y así no hay import circular: la tienda usa este explorador)
const CatalogoTienda = React.lazy(() => import('./CatalogoTienda'));
import './CatalogoFFE.css';

const getToken = () => localStorage.getItem('tuvi_token');
const api = async (url, opts = {}) => {
  const res = await fetch(url, { ...opts, headers: { ...opts.headers, 'Authorization': `Bearer ${getToken()}` } });
  return res.json();
};
const IMG_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
const isImage = (name) => IMG_EXTS.includes((name || '').split('.').pop().toLowerCase());
const formatSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
};

const BUCKETS = [
  { value: 'catalogo-ffe', label: 'Catalogo FFE' },
  { value: 'planos', label: 'Planos' },
];

export default function CatalogoFFE({ onSelectImage, selectionMode = false, bucket: bucketInicial = 'catalogo-ffe', allowBucketSwitch = false }) {
  const { success, error } = useNotification();
  const [bucket, setBucket] = useState(bucketInicial);
  const [verTienda, setVerTienda] = useState(false);
  const [prefix, setPrefix] = useState('');
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(null); // null = no búsqueda, [] = sin resultados
  const [searchLoading, setSearchLoading] = useState(false);

  // Forms
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [renaming, setRenaming] = useState(null); // { key, name, type }
  const [renameName, setRenameName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  // Multi-selección para MOVER archivos (independiente del selectionMode de FFE)
  const [seleccionados, setSeleccionados] = useState(() => new Set());
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [moverModal, setMoverModal] = useState(false);
  const [moverPrefix, setMoverPrefix] = useState('');
  const [moverFolders, setMoverFolders] = useState([]);
  const [moviendo, setMoviendo] = useState(false);
  const dragKeysRef = useRef([]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api(`${UPLOAD_URL}/catalogo/list?prefix=${encodeURIComponent(prefix)}&bucket=${bucket}`);
      if (d.success) {
        setFolders(d.folders || []);
        setFiles(d.files || []);
      } else error(d.message);
    } catch { error('Error cargando catalogo'); }
    finally { setLoading(false); }
  }, [prefix, bucket, error]);

  useEffect(() => { cargar(); }, [cargar]);

  // Breadcrumb
  const parts = prefix.split('/').filter(Boolean);
  const breadcrumb = [{ label: 'Catalogo FFE', prefix: '' }];
  parts.forEach((p, i) => {
    breadcrumb.push({ label: p, prefix: parts.slice(0, i + 1).join('/') + '/' });
  });

  const navigateTo = (p) => { setPrefix(p); setShowNewFolder(false); setRenaming(null); };

  const crearCarpeta = async () => {
    if (!newFolderName.trim()) return;
    const path = prefix + newFolderName.trim().replace(/\//g, '-') + '/';
    const d = await api(`${UPLOAD_URL}/catalogo/folder?bucket=${bucket}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ path }),
    });
    if (d.success) { success('Carpeta creada'); setNewFolderName(''); setShowNewFolder(false); cargar(); }
    else error(d.message);
  };

  const subirArchivos = async (fileList) => {
    if (!fileList?.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      for (const f of fileList) fd.append('files', f);
      const res = await fetch(`${UPLOAD_URL}/catalogo/upload?prefix=${encodeURIComponent(prefix)}&bucket=${bucket}`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${getToken()}` }, body: fd,
      });
      const d = await res.json();
      if (d.success) { success(d.message); cargar(); }
      else error(d.message);
    } catch { error('Error subiendo archivos'); }
    finally { setUploading(false); }
  };

  const eliminar = async (key, type) => {
    const msg = type === 'folder' ? 'Eliminar esta carpeta y todo su contenido?' : 'Eliminar este archivo?';
    if (!window.confirm(msg)) return;
    const d = await api(`${UPLOAD_URL}/catalogo/delete?key=${encodeURIComponent(key)}&bucket=${bucket}`, { method: 'DELETE' });
    if (d.success) { success('Eliminado'); cargar(); }
    else error(d.message);
  };

  const renombrar = async () => {
    if (!renameName.trim() || !renaming) return;
    let newKey;
    if (renaming.type === 'folder') {
      const parentPrefix = renaming.key.split('/').slice(0, -2).join('/');
      newKey = (parentPrefix ? parentPrefix + '/' : '') + renameName.trim() + '/';
    } else {
      const ext = renaming.name.includes('.') ? '.' + renaming.name.split('.').pop() : '';
      newKey = prefix + renameName.trim() + ext;
    }
    const d = await api(`${UPLOAD_URL}/catalogo/rename?bucket=${bucket}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ oldKey: renaming.key, newKey }),
    });
    if (d.success) { success('Renombrado'); setRenaming(null); cargar(); }
    else error(d.message);
  };

  const getImageUrl = (key) => `${UPLOAD_URL}/catalogo/img/${bucket}/${key}`;

  // Búsqueda recursiva en todo el bucket con debounce
  useEffect(() => {
    const term = searchTerm.trim();
    if (!term) { setSearchResults(null); setSearchLoading(false); return; }
    setSearchLoading(true);
    const handle = setTimeout(async () => {
      try {
        const d = await api(`${UPLOAD_URL}/catalogo/search?q=${encodeURIComponent(term)}&bucket=${bucket}`);
        setSearchResults(d.success ? (d.files || []) : []);
      } catch { setSearchResults([]); }
      finally { setSearchLoading(false); }
    }, 300);
    return () => clearTimeout(handle);
  }, [searchTerm, bucket]);

  // Navegar a la carpeta contenedora de un resultado y preseleccionar si es imagen
  const abrirResultado = (result) => {
    const esImg = isImage(result.name);
    if (selectionMode && esImg && onSelectImage) {
      onSelectImage(getImageUrl(result.key), result.key, result.name);
      return;
    }
    // Navegar a la carpeta contenedora
    setPrefix(result.folderPrefix || '');
    setSearchTerm(''); // limpia búsqueda
    setSearchResults(null);
    if (esImg) {
      setTimeout(() => setPreview({ key: result.key, name: result.name }), 150);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    subirArchivos(e.dataTransfer.files);
  };

  // ── Mover archivos: multi-selección con check + arrastrar a carpeta + botón "Mover a..." ──
  const toggleSel = (key, e) => {
    if (e) e.stopPropagation();
    setSeleccionados(prev => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };
  const limpiarSel = () => setSeleccionados(new Set());

  // Mueve una lista de keys (archivos y/o carpetas) a la carpeta destino (prefix con '/').
  // Mover = rename a destino+nombre (el backend hace copyObject + deleteFile, recursivo si es carpeta).
  const moverArchivos = async (keys, destinoPrefix) => {
    const lista = (keys || []).filter(Boolean);
    if (!lista.length) return;
    const destino = destinoPrefix || '';
    setMoviendo(true);
    let ok = 0, err = 0, saltados = 0, invalidos = 0;
    for (const key of lista) {
      const esCarpeta = key.endsWith('/');
      let newKey;
      if (esCarpeta) {
        // No se puede mover una carpeta dentro de sí misma o de un descendiente suyo
        if (destino === key || destino.startsWith(key)) { invalidos++; continue; }
        const nombre = key.replace(/\/+$/, '').split('/').pop();
        newKey = destino + nombre + '/';
      } else {
        newKey = destino + key.split('/').pop();
      }
      if (newKey === key) { saltados++; continue; } // ya está en esa carpeta
      try {
        const d = await api(`${UPLOAD_URL}/catalogo/rename?bucket=${bucket}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ oldKey: key, newKey }),
        });
        if (d.success) ok++; else err++;
      } catch { err++; }
    }
    setMoviendo(false);
    limpiarSel();
    setMoverModal(false);
    if (ok) success(`${ok} elemento(s) movido(s)`);
    if (err) error(`${err} no se pudieron mover`);
    if (invalidos) error('No puedes mover una carpeta dentro de sí misma');
    if (!ok && !err && !invalidos && saltados) success('Ya estaban en esa carpeta');
    cargar();
  };

  // Drag & drop: al arrastrar un archivo, se mueve la selección (o solo ese archivo).
  const onDragStartFile = (key, e) => {
    const keys = seleccionados.has(key) ? [...seleccionados] : [key];
    dragKeysRef.current = keys;
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', 'mover-archivos'); } catch { /* noop */ }
  };
  const onDropFolder = (folderKey, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverFolder(null);
    const keys = dragKeysRef.current;
    dragKeysRef.current = [];
    if (keys && keys.length) moverArchivos(keys, folderKey);
  };

  // Modal selector de carpeta destino ("Mover a...")
  const abrirMoverModal = async () => { setMoverModal(true); await moverNavegar(''); };
  const moverNavegar = async (p) => {
    setMoverPrefix(p);
    try {
      const d = await api(`${UPLOAD_URL}/catalogo/list?prefix=${encodeURIComponent(p)}&bucket=${bucket}`);
      setMoverFolders(d?.folders || []);
    } catch { setMoverFolders([]); }
  };

  return (
    <div className="cat-container" onDragOver={e => e.preventDefault()} onDrop={handleDrop}>
      {verTienda && (
        <React.Suspense fallback={<div className="cat-loading">Abriendo catálogo...</div>}>
          <CatalogoTienda onClose={() => setVerTienda(false)} />
        </React.Suspense>
      )}
      {/* Header */}
      {!selectionMode && (
        <div className="cat-header">
          <div>
            <h2 className="cat-title">Catalogo FFE</h2>
            <p className="cat-subtitle">Imagenes y archivos para fichas FFE</p>
          </div>
          <button className="cat-btn cat-btn-tienda" onClick={() => setVerTienda(true)} title="Ver el catálogo como tienda en línea (para mostrar al cliente)">
            🛍 Ver catálogo tienda
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="cat-toolbar">
        <div className="cat-breadcrumb">
          {breadcrumb.map((b, i) => (
            <span key={i}>
              {i > 0 && <span className="cat-bc-sep">/</span>}
              <button className={`cat-bc-btn ${i === breadcrumb.length - 1 ? 'active' : ''}`}
                onClick={() => navigateTo(b.prefix)}>{b.label}</button>
            </span>
          ))}
        </div>
        <div className="cat-toolbar-actions">
          {allowBucketSwitch && (
            <select className="cat-bucket-select" value={bucket} onChange={e => { setBucket(e.target.value); setPrefix(''); setSearchTerm(''); }}>
              {BUCKETS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          )}
          <input type="text" className="cat-search" placeholder="Buscar..." value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)} />
          <button className="cat-btn cat-btn-sm" onClick={() => setViewMode(v => v === 'grid' ? 'list' : 'grid')}>
            {viewMode === 'grid' ? '☰' : '⊞'}
          </button>
          <button className="cat-btn cat-btn-sm cat-btn-outline" onClick={() => setShowNewFolder(true)}>+ Carpeta</button>
          <button className="cat-btn cat-btn-sm cat-btn-primary" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? 'Subiendo...' : '+ Subir Archivos'}
          </button>
          <input ref={fileInputRef} type="file" multiple hidden onChange={e => subirArchivos(e.target.files)} accept="image/*,.pdf,.doc,.docx" />
        </div>
      </div>

      {/* Barra de selección (mover archivos) */}
      {!selectionMode && seleccionados.size > 0 && (
        <div className="cat-sel-bar">
          <span className="cat-sel-count">✓ {seleccionados.size} seleccionado(s)</span>
          <button className="cat-btn cat-btn-sm cat-btn-primary" onClick={abrirMoverModal} disabled={moviendo}>
            📁 Mover a...
          </button>
          <button className="cat-btn cat-btn-sm" onClick={limpiarSel}>Cancelar</button>
          <span className="cat-sel-hint">Tip: también puedes arrastrar archivos o carpetas a otra carpeta</span>
        </div>
      )}

      {/* New Folder */}
      {showNewFolder && (
        <div className="cat-new-folder">
          <input type="text" value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
            placeholder="Nombre de la carpeta" autoFocus
            onKeyDown={e => { if (e.key === 'Enter') crearCarpeta(); if (e.key === 'Escape') setShowNewFolder(false); }} />
          <button className="cat-btn cat-btn-sm cat-btn-primary" onClick={crearCarpeta}>Crear</button>
          <button className="cat-btn cat-btn-sm" onClick={() => setShowNewFolder(false)}>Cancelar</button>
        </div>
      )}

      {/* Rename */}
      {renaming && (
        <div className="cat-new-folder">
          <span className="cat-rename-label">Renombrar: {renaming.name}</span>
          <input type="text" value={renameName} onChange={e => setRenameName(e.target.value)}
            placeholder="Nuevo nombre" autoFocus
            onKeyDown={e => { if (e.key === 'Enter') renombrar(); if (e.key === 'Escape') setRenaming(null); }} />
          <button className="cat-btn cat-btn-sm cat-btn-primary" onClick={renombrar}>Renombrar</button>
          <button className="cat-btn cat-btn-sm" onClick={() => setRenaming(null)}>Cancelar</button>
        </div>
      )}

      {/* Content */}
      {searchResults !== null ? (
        // === MODO BÚSQUEDA (recursiva) ===
        searchLoading ? (
          <div className="cat-loading">Buscando en todo el bucket...</div>
        ) : searchResults.length === 0 ? (
          <div className="cat-empty">
            <div className="cat-empty-icon">🔍</div>
            <p>Sin resultados para "{searchTerm}"</p>
            <p className="cat-empty-hint">Intenta con otra palabra</p>
          </div>
        ) : (
          <div className={`cat-content ${viewMode}`}>
            <div className="cat-search-info">
              {searchResults.length} resultado(s). Clic para abrir la carpeta contenedora{selectionMode ? ' o seleccionar' : ''}.
            </div>
            {searchResults.map(r => (
              <div key={r.key} className={`cat-item cat-file ${isImage(r.name) ? 'cat-image' : ''}`}>
                <div className="cat-item-icon" onClick={() => abrirResultado(r)} style={{ cursor: 'pointer' }}>
                  {isImage(r.name) ? (
                    <img src={getImageUrl(r.key)} alt={r.name} className="cat-thumb" loading="lazy" />
                  ) : (
                    <span className="cat-file-icon">📄</span>
                  )}
                </div>
                <div className="cat-item-info" onClick={() => abrirResultado(r)} style={{ cursor: 'pointer' }}>
                  <span className="cat-item-name">{r.name}</span>
                  <span className="cat-item-meta" title={r.folderPrefix}>📁 {r.folderPrefix || '/'}</span>
                </div>
                <div className="cat-item-actions">
                  {selectionMode && isImage(r.name) && (
                    <button className="cat-btn cat-btn-sm cat-btn-select" onClick={() => onSelectImage(getImageUrl(r.key), r.key, r.name)}>Seleccionar</button>
                  )}
                  <button className="cat-btn cat-btn-sm" onClick={() => abrirResultado(r)} title="Ir a la carpeta">📂 Abrir</button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : loading ? (
        <div className="cat-loading">Cargando...</div>
      ) : (
        <div className={`cat-content ${viewMode}`}>
          {/* Folders */}
          {folders.map(f => {
            const sel = seleccionados.has(f.key);
            const puedeMover = !selectionMode;
            return (
            <div key={f.key}
              className={`cat-item cat-folder ${sel ? 'cat-file-sel' : ''} ${dragOverFolder === f.key ? 'cat-folder-dragover' : ''}`}
              draggable={puedeMover}
              onDragStart={puedeMover ? (e) => onDragStartFile(f.key, e) : undefined}
              onDoubleClick={() => navigateTo(f.key)}
              onDragOver={selectionMode ? undefined : (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (!dragKeysRef.current.includes(f.key)) setDragOverFolder(f.key); }}
              onDragLeave={selectionMode ? undefined : () => setDragOverFolder(null)}
              onDrop={selectionMode ? undefined : (e) => onDropFolder(f.key, e)}>
              {puedeMover && (
                <label className="cat-check" title="Seleccionar para mover" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={sel} onChange={(e) => toggleSel(f.key, e)} />
                </label>
              )}
              <div className="cat-item-icon" onClick={() => navigateTo(f.key)}>📁</div>
              <div className="cat-item-info" onClick={() => navigateTo(f.key)}>
                <span className="cat-item-name">{f.name}</span>
                <span className="cat-item-meta">Carpeta</span>
              </div>
              <div className="cat-item-actions">
                <button className="cat-action-btn" onClick={(e) => { e.stopPropagation(); setRenaming({ key: f.key, name: f.name, type: 'folder' }); setRenameName(f.name); }} title="Renombrar">✏️</button>
                <button className="cat-action-btn cat-action-danger" onClick={(e) => { e.stopPropagation(); eliminar(f.key, 'folder'); }} title="Eliminar">🗑️</button>
              </div>
            </div>
            );
          })}

          {/* Files */}
          {files.map(f => {
            const sel = seleccionados.has(f.key);
            const puedeMover = !selectionMode; // multi-selección/arrastrar solo fuera del modo picker
            return (
            <div key={f.key}
              className={`cat-item cat-file ${isImage(f.name) ? 'cat-image' : ''} ${sel ? 'cat-file-sel' : ''}`}
              draggable={puedeMover}
              onDragStart={puedeMover ? (e) => onDragStartFile(f.key, e) : undefined}>
              {puedeMover && (
                <label className="cat-check" title="Seleccionar para mover" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={sel} onChange={(e) => toggleSel(f.key, e)} />
                </label>
              )}
              <div className="cat-item-icon" onClick={() => {
                if (selectionMode && isImage(f.name) && onSelectImage) {
                  onSelectImage(getImageUrl(f.key), f.key, f.name);
                } else if (isImage(f.name)) {
                  setPreview(f);
                }
              }}>
                {isImage(f.name) ? (
                  <img src={getImageUrl(f.key)} alt={f.name} className="cat-thumb" loading="lazy" />
                ) : (
                  <span className="cat-file-icon">📄</span>
                )}
              </div>
              <div className="cat-item-info">
                <span className="cat-item-name">{f.name}</span>
                <span className="cat-item-meta">{formatSize(f.size)}</span>
              </div>
              <div className="cat-item-actions">
                {selectionMode && isImage(f.name) && (
                  <button className="cat-btn cat-btn-sm cat-btn-select" onClick={() => onSelectImage(getImageUrl(f.key), f.key, f.name)}>Seleccionar</button>
                )}
                <button className="cat-action-btn" onClick={() => { setRenaming({ key: f.key, name: f.name, type: 'file' }); setRenameName(f.name.replace(/\.[^.]+$/, '')); }} title="Renombrar">✏️</button>
                <button className="cat-action-btn cat-action-danger" onClick={() => eliminar(f.key, 'file')} title="Eliminar">🗑️</button>
              </div>
            </div>
            );
          })}

          {folders.length === 0 && files.length === 0 && (
            <div className="cat-empty">
              <div className="cat-empty-icon">📂</div>
              <p>Carpeta vacia</p>
              <p className="cat-empty-hint">Arrastra archivos aqui o usa los botones de arriba</p>
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {preview && (
        <div className="cat-preview-overlay" onClick={() => setPreview(null)}>
          <div className="cat-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="cat-preview-header">
              <span>{preview.name}</span>
              <button className="cat-preview-close" onClick={() => setPreview(null)}>&times;</button>
            </div>
            <div className="cat-preview-body">
              <img src={getImageUrl(preview.key)} alt={preview.name} />
            </div>
          </div>
        </div>
      )}

      {/* Modal: elegir carpeta destino para mover */}
      {moverModal && (
        <div className="cat-preview-overlay" onClick={() => !moviendo && setMoverModal(false)}>
          <div className="cat-mover-modal" onClick={e => e.stopPropagation()}>
            <div className="cat-preview-header">
              <span>Mover {seleccionados.size} elemento(s) a...</span>
              <button className="cat-preview-close" onClick={() => setMoverModal(false)} disabled={moviendo}>&times;</button>
            </div>
            <div className="cat-mover-breadcrumb">
              {(() => {
                const mparts = moverPrefix.split('/').filter(Boolean);
                const mbc = [{ label: '🏠 Raíz', prefix: '' }];
                mparts.forEach((p, i) => mbc.push({ label: p, prefix: mparts.slice(0, i + 1).join('/') + '/' }));
                return mbc.map((b, i) => (
                  <span key={i}>
                    {i > 0 && <span className="cat-bc-sep">/</span>}
                    <button className={`cat-bc-btn ${i === mbc.length - 1 ? 'active' : ''}`}
                      onClick={() => moverNavegar(b.prefix)}>{b.label}</button>
                  </span>
                ));
              })()}
            </div>
            <div className="cat-mover-list">
              {moverFolders.length === 0 ? (
                <div className="cat-mover-empty">No hay subcarpetas aquí. Puedes mover a esta ubicación.</div>
              ) : moverFolders.map(f => (
                <button key={f.key} className="cat-mover-folder" onClick={() => moverNavegar(f.key)}>
                  <span>📁 {f.name}</span>
                  <span className="cat-mover-enter">Abrir ›</span>
                </button>
              ))}
            </div>
            <div className="cat-mover-actions">
              <button className="cat-btn cat-btn-sm" onClick={() => setMoverModal(false)} disabled={moviendo}>Cancelar</button>
              <button className="cat-btn cat-btn-sm cat-btn-primary"
                onClick={() => moverArchivos([...seleccionados], moverPrefix)} disabled={moviendo}>
                {moviendo ? 'Moviendo...' : `Mover aquí: ${moverPrefix ? moverPrefix.split('/').filter(Boolean).pop() : 'Raíz'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
